import { Typography } from "antd";
import { companyBlock, intro, lastUpdated, legalTitle, sectionBody, sectionHeading } from "../LegalPage.styles.ts";

const { Title, Paragraph, Text } = Typography;

export const PrivacyBg = () => (
  <article>
    <Title className={legalTitle} level={1}>
      Политика за поверителност
    </Title>
    <Text className={lastUpdated}>Последна актуализация: 6 май 2026 г.</Text>

    <Paragraph className={intro}>
      Тази Политика за поверителност описва как <strong>Фууд Сейфти Консултинг ЕООД</strong> („Calculabel“, „ние“)
      събира, използва и защитава лични данни при ползването на услугата Calculabel („Услугата“) на calculabel.com и
      свързаните домейни. По смисъла на Регламент (ЕС) 2016/679 (GDPR) и Закона за защита на личните данни ние сме
      администратор на личните данни, описани по-долу.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      1. За тази политика
    </Title>
    <Paragraph className={sectionBody}>
      Политиката се прилага за всички посетители и регистрирани потребители на Услугата. Със създаването на профил или с
      друго ползване на Услугата вие потвърждавате, че сте се запознали с настоящата политика. Възможно е тя да бъде
      актуализирана периодично — вижте раздел „Промени в политиката“.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      2. Какви данни събираме
    </Title>
    <div className={sectionBody}>
      <Paragraph>Събираме и обработваме следните категории лични данни:</Paragraph>
      <ul>
        <li>
          <strong>Данни за профил</strong> — имейл адрес, пълно име, езикова настройка и екипът, към който принадлежите.
        </li>
        <li>
          <strong>Данни за удостоверяване</strong> — краткотрайни еднократни кодове за вход без парола чрез имейл и
          криптирана сесийна бисквитка, която се поддържа, докато сте вписани.
        </li>
        <li>
          <strong>Съдържание, което създавате</strong> — рецепти, съставки, производители, файлове, етикети и друго
          съдържание, добавено в работното пространство на екипа ви.
        </li>
        <li>
          <strong>Данни за ползване</strong> — сървърни записи на заявките към Услугата, доклади за грешки и обобщени
          показатели, необходими за работата и сигурността на платформата.
        </li>
        <li>
          <strong>Технически данни</strong> — IP адрес, тип браузър и потребителски агент, устройство и операционна
          система, приблизително местоположение по IP, използвани за сигурност и предотвратяване на злоупотреби.
        </li>
      </ul>
    </div>

    <Title className={sectionHeading} level={4}>
      3. На какво основание обработваме данните
    </Title>
    <div className={sectionBody}>
      <ul>
        <li>
          <strong>Изпълнение на договор</strong> (чл. 6, §1, б. „б“ от GDPR) — за създаване и поддържане на профила ви,
          за удостоверяване и за предоставяне на функциите, които използвате.
        </li>
        <li>
          <strong>Законни интереси</strong> (чл. 6, §1, б. „е“ от GDPR) — за сигурността на Услугата, предотвратяване на
          измами и злоупотреби, диагностика и подобряване на продукта. Имате право на възражение срещу обработка на
          основание законен интерес (вижте „Вашите права“).
        </li>
        <li>
          <strong>Спазване на правно задължение</strong> (чл. 6, §1, б. „в“ от GDPR) — за изпълнение на изискванията на
          българското и европейското законодателство (например счетоводни и данъчни задължения при платените планове).
        </li>
        <li>
          <strong>Съгласие</strong> (чл. 6, §1, б. „а“ от GDPR) — само в случаите, когато изрично го поискаме. Имате
          право да оттеглите съгласието си по всяко време, без това да засяга законосъобразността на обработката преди
          оттеглянето.
        </li>
      </ul>
    </div>

    <Title className={sectionHeading} level={4}>
      4. Срокове на съхранение
    </Title>
    <div className={sectionBody}>
      <ul>
        <li>
          <strong>Профил и съдържание</strong> — докато профилът ви е активен. След изтриване на профила запазваме
          остатъчни данни до 12 месеца за възстановяване на профила и за разследване на злоупотреби, след което те се
          изтриват или анонимизират.
        </li>
        <li>
          <strong>Резервни копия</strong> — криптираните резервни копия се пазят до 90 дни и се презаписват по график.
        </li>
        <li>
          <strong>Сървърни регистри</strong> — до 90 дни за целите на сигурността и експлоатацията.
        </li>
        <li>
          <strong>Счетоводни и данъчни документи</strong> — съхраняват се 10 години в съответствие със Закона за
          счетоводството (при наличие на платени планове).
        </li>
      </ul>
    </div>

    <Title className={sectionHeading} level={4}>
      5. Споделяне с трети страни (подизпълнители)
    </Title>
    <div className={sectionBody}>
      <Paragraph>
        Не продаваме личните ви данни. Споделяме ги само с внимателно подбрани доставчици на услуги, които ни помагат да
        управляваме Calculabel, всеки от които е обвързан с подходящи договорни условия за обработка на данни:
      </Paragraph>
      <ul>
        <li>
          <strong>OVH</strong> (Франция, ЕС) — сървърна инфраструктура и хостинг.
        </li>
        <li>
          <strong>DigitalOcean</strong> (регион в ЕС) — приложен хостинг и виртуални машини.
        </li>
        <li>
          <strong>Cloudflare</strong> — DNS, мрежа за доставка на съдържание (CDN), TLS терминация, защитна стена за уеб
          приложения и защита от DDoS атаки.
        </li>
        <li>
          <strong>ZeptoMail</strong> (Zoho) — доставка на транзакционни имейли (връзки за вход, известия).
        </li>
        <li>
          <strong>Google LLC</strong> (Google Cloud, Gemini API) — обработка с изкуствен интелект на качени снимки на
          продуктови етикети и рецепти за извличане на данни за съставки и хранителни стойности. Съгласно условията на
          Google потребителските данни, изпратени към Gemini през API, не се използват за обучение на моделите на
          Google.
        </li>
      </ul>
      <Paragraph>
        <strong>Не използваме</strong> услуги за анализ, реклама, маркетинг или социални мрежи от трети страни.
      </Paragraph>
      <Paragraph>
        Може да разкрием лични данни и при законово задължение, по съдебно разпореждане или по искане на компетентен
        държавен орган.
      </Paragraph>
    </div>

    <Title className={sectionHeading} level={4}>
      6. Международно предаване на данни
    </Title>
    <Paragraph className={sectionBody}>
      Основната ни инфраструктура (OVH и DigitalOcean) е разположена в Европейския съюз. Cloudflare, ZeptoMail и Google
      (Cloud / Gemini) оперират глобални мрежи, които може да маршрутизират или обработват данни извън ЕС/ЕИП,
      включително в Съединените щати. Такива предавания са защитени от стандартните договорни клаузи на Европейската
      комисия и от условията за защита на данните, публикувани от съответните доставчици.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      7. Вашите права
    </Title>
    <div className={sectionBody}>
      <Paragraph>Съгласно GDPR имате право да:</Paragraph>
      <ul>
        <li>получавате достъп до личните си данни и копие от тях;</li>
        <li>искате коригиране на неточни или непълни данни;</li>
        <li>искате изтриване („право да бъдете забравени“), когато е приложимо;</li>
        <li>искате ограничаване на обработката;</li>
        <li>възразявате срещу обработка на основание законен интерес;</li>
        <li>получавате данните си в преносим, машинночетим формат;</li>
        <li>оттегляте съгласие по всяко време, когато обработката се извършва на негово основание;</li>
        <li>
          подавате жалба до Комисията за защита на личните данни (КЗЛД), бул. „Проф. Цветан Лазаров“ № 2, 1592 София —
          <a href="https://www.cpdp.bg"> www.cpdp.bg</a>.
        </li>
      </ul>
      <Paragraph>
        За да упражните някое от тези права, пишете на{" "}
        <a href="mailto:support@calculabel.com">support@calculabel.com</a>. Ще отговорим в рамките на един месец, както
        изисква GDPR.
      </Paragraph>
    </div>

    <Title className={sectionHeading} level={4}>
      8. Бисквитки и подобни технологии
    </Title>
    <Paragraph className={sectionBody}>
      Calculabel използва <strong>само строго необходими бисквитки</strong>: криптирана сесийна бисквитка за поддържане
      на вашия вход и малка бисквитка за запомняне на езиковата ви настройка. <strong>Не използваме</strong> бисквитки
      за анализ, реклама, маркетинг или проследяване от трети страни. По тази причина не е необходимо съгласие за
      бисквитки по смисъла на Директивата за електронните комуникации и GDPR.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      9. Сигурност
    </Title>
    <Paragraph className={sectionBody}>
      Защитаваме данните ви чрез HTTPS / HTTP/3 криптиране при пренос, криптирани сесийни бисквитки, удостоверяване без
      парола чрез еднократни краткотрайни имейл връзки, мрежови защитни стени и контрол на достъпа, както и редовни
      резервни копия. Никоя система не е напълно неуязвима, но непрекъснато прилагаме съвременни мерки за защита.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      10. Деца
    </Title>
    <Paragraph className={sectionBody}>
      Calculabel не е насочен към деца под 16-годишна възраст и не събираме съзнателно лични данни от тях. Ако смятате,
      че дете ни е предоставило свои данни, моля свържете се с нас и ще ги изтрием.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      11. Промени в политиката
    </Title>
    <Paragraph className={sectionBody}>
      Възможно е периодично да актуализираме тази политика. Датата „Последна актуализация“ най-горе отразява последната
      версия. При съществени промени ще предоставим допълнително уведомление — например чрез банер в приложението или
      имейл до регистрираните потребители — преди промените да влязат в сила.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      12. Контакт
    </Title>
    <Paragraph className={sectionBody}>
      Въпроси относно тази политика или начина, по който обработваме вашите данни? Пишете ни на{" "}
      <a href="mailto:support@calculabel.com">support@calculabel.com</a>.
    </Paragraph>

    <Paragraph className={companyBlock}>
      Администратор на лични данни: Фууд Сейфти Консултинг ЕООД
      <br />
      ЕИК: 204435984
      <br />
      ДДС №: BG204435984
      <br />
      Адрес: ул. Тракия № 34, 1527 София, България
      <br />
      Имейл: <a href="mailto:support@calculabel.com">support@calculabel.com</a>
    </Paragraph>
  </article>
);
